import QRCodeLib from 'qrcode';
import QRCodeStyling from 'qr-code-styling';

/**
 * Generate a PNG Blob for a QR code of the given text.
 * Options are tuned for on-device scanning and clean rendering.
 */
export async function generateQrPngBlob(text: string, size = 512, margin = 2): Promise<Blob> {
    try {
        // Generate a crisp square QR (no rounded modules) for a sharp, professional look
        const qr = new QRCodeStyling({
            width: size,
            height: size,
            type: 'canvas',
            data: text,
            margin,
            qrOptions: { errorCorrectionLevel: 'Q' },
            dotsOptions: { type: 'classy', color: '#000000' } as any,
            cornersSquareOptions: { type: 'square', color: '#000000' } as any,
            cornersDotOptions: { type: 'square', color: '#000000' } as any,
            backgroundOptions: { color: '#ffffffff' },
        });
        const canvas = document.createElement('canvas');
        await qr.update({ data: text, width: size, height: size });
        // Append to off-DOM element to ensure rendering
        const tmp = document.createElement('div');
        qr.append(tmp);
        // Grab the canvas element generated
        const c = tmp.querySelector('canvas') as HTMLCanvasElement | null;
        if (c) {
            const blob: Blob | null = await new Promise((resolve) => c.toBlob((b) => resolve(b), 'image/png'));
            if (blob) return blob;
        }
        // Fallback to QRCodeLib if canvas extraction failed
    } catch {
        // ignore and fallback below
    }
    const dataUrl = await QRCodeLib.toDataURL(text, {
        errorCorrectionLevel: 'M',
        width: size,
        margin,
        color: {
            dark: '#000000',
            light: '#ffffff00',
        },
    } as any);
    const res = await fetch(dataUrl);
    return await res.blob();
}

export async function downloadQrPng(text: string, filename = 'qr.png', size = 1024) {
    const blob = await generateQrPngBlob(text, size);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
}

export async function shareQrPng(text: string, title = 'QR Code', urlToShare?: string) {
    try {
        const blob = await generateQrPngBlob(text, 1024);
        const file = new File([blob], 'qr.png', { type: 'image/png' });
        if ((navigator as any).share && (navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
            await (navigator as any).share({
                title,
                text: urlToShare || text,
                files: [file],
            });
            return true;
        }
    } catch (e) {
        // fall through to download
    }
    await downloadQrPng(text);
    return false;
}
